import React from 'react';
import { CategoryBorderCard } from '../../containers/CategoryBorderCard';
import classNames from 'classnames';
import classes from './BasePostCard.module.scss';
import { ButtonMenu } from '../../actions/ButtonMenu';
import { CardOrientationEnum, PostCardViewProps } from './BasePostCard.view';
import { SquareButtonIconsEnum } from '../../../constants/square-button-icons';
import { extractDomain } from '../../../helpers/extractDomainFromUrl';
import { getCardText } from '../helpers/getCardText';
import { PostCardStatusTable } from '../PostCardStatusTable';
import { PostScheduleViewContext } from '../../../modules/Publish/PostSchedule/PostSchedule.controller';
import { Debugger } from '../../containers/Debugger';
import { Tooltip } from 'components/actions/Tooltip';
import { PostCardError } from '../PostCardError';
import { PostCardStatisticsTable } from '../PostCardStatisticsTable';
import { PostStatus, PostTiming } from '../../../models/graphql';
import { IsPostPaused } from '../PostCardStatusButton/PostCardStatusButton.view';
import scssVars from '../../../assets/scss/exportedVars.module.scss';
import { PostCardLinkPreviewSizeEnum } from '../PostCardLinkPreview/PostCardLinkPreview.view';
import { PostCardLinkPreview } from '../PostCardLinkPreview';
import { PostScheduleHelpers } from '../../../modules/Publish/PostSchedule/postScheduleHelpers';
import { PostCardMedia } from '../PostCardMedia';
import { PostCardStatusIcon } from '../PostCardStatusIcon';

export const BasePostCardPortraitView = (props: PostCardViewProps): JSX.Element => {
  const {
    post,
    isDetailedPastPost,
    context,
    menuListItems,
    reschedulePostHandler,
    isPostStatusUnqueued,
    openDetailsModal,
    showBarGraph,
    orientation,
    hasHoverBackground
  } = props;

  const isPostPaused = IsPostPaused(post);

  // For certain views, we do not want to show the icons outside of profile/date
  const showMinimalStatusIcons =
    isPostPaused ||
    isPostStatusUnqueued ||
    (post.status !== PostStatus.ACTIVE && post.status !== PostStatus.PUBLISHED && post.status !== PostStatus.FAILED);

  const cardClasses = classNames(classes.BasePostCard, classes[`BasePostCard${orientation}`], {
    [classes.BasePostCardHover]: hasHoverBackground
  });
  const cardBodyClasses = classNames('cell auto', classes.Wrapper);
  const cardMediaClasses = classNames('cell shrink', classes.Media);
  const cardTextClasses = classNames(
    classes.PostText,
    classes.PostTextPortrait,
    classes.CardText,
    classes.CardTextPortrait,
    classes.TextWrapper
  );
  const linkButtonClasses = classNames(classes.LinkButton);

  const stackedCardsClasses = classNames({
    [classes.StackedCardsContainerLandscape]: orientation === CardOrientationEnum.LANDSCAPE,
    [classes.StackedCardsContainerPortrait]: orientation === CardOrientationEnum.PORTRAIT
  });

  const photoThumbnail = post.variationMeta?.currentVariation.media[0]
    ? post.variationMeta.currentVariation.media[0].thumbnailUrl
    : undefined;

  const postCardMediaValues = post.variationMeta?.currentVariation.media[0]
    ? PostScheduleHelpers.mapCorePostToPostCardType(post, true)
    : undefined;

  const isStacked = post.variationMeta?.hasMultiple || false;

  const hasStatusIcon =
    !isPostPaused &&
    post.timing !== PostTiming.QUEUE &&
    post.status !== PostStatus.PUBLISHED &&
    (post.status === PostStatus.ACTIVE || post.status === PostStatus.FAILED) &&
    (context === PostScheduleViewContext.LIST || context === PostScheduleViewContext.WEEK);
  const statusIconClasses = classNames('cell', 'shrink', classes.StatusIcon);

  const showPostStatistics =
    isDetailedPastPost || context === PostScheduleViewContext.LIBRARY || post.status === PostStatus.PUBLISHED;

  return (
    <>
      <CategoryBorderCard borderColor={post.category.color} borderPosition={'Top'} applyCardBorder={true}>
        <Debugger tooltipContent={`Post ID: ${post.id}`} modalContent={post} />
        <div className={cardClasses}>
          <div className={cardBodyClasses}>
            <div className={'grid-x align-middle margin-top-small-8 margin-bottom-small-8'}>
              {hasStatusIcon ? (
                <div className={statusIconClasses}>
                  <PostCardStatusIcon post={post} />
                </div>
              ) : undefined}
              <div className={classNames('cell', 'auto', hasStatusIcon ? classes.TitleWidth : classes.TitleMaxWidth)}>
                <div className={classNames('one-liner', classes.CategoryName)}>{post.category.name}</div>
              </div>
            </div>
            <div className={cardTextClasses} dangerouslySetInnerHTML={{ __html: getCardText(post) }} />
            {photoThumbnail ? (
              <>
                <div className={cardMediaClasses}>
                  {postCardMediaValues && <PostCardMedia {...postCardMediaValues} orientation={orientation} />}
                </div>
                {post.link?.url && (
                  <>
                    <Tooltip
                      type={'regular'}
                      pointer={true}
                      showDelay={20}
                      hideDelay={0}
                      className={'p-tooltip-dark-pointer'}
                      target={`#link-tooltip-${post.id}`}
                      autoZIndex={false}
                      autoHide={false}
                      style={{ zIndex: scssVars['z-index-overmodal'] }}>
                      <>
                        {post.link.urlBitlink || post.link.initialUrlBitlink ? (
                          <>
                            <div className={classNames('margin-bottom-small-8', classes.BitlyTooltip)}>
                              <span>Bitly link:</span> {post.link.url}
                            </div>
                            <div className={classNames('margin-top-small-8', classes.BitlyTooltip)}>
                              <span>Redirected link:</span> {post.link.expandedUrl}
                            </div>
                          </>
                        ) : (
                          <>{post.link.url}</>
                        )}
                      </>
                    </Tooltip>
                    <div className={classes.LinkButtonWrapper}>
                      <div className={linkButtonClasses} id={`link-tooltip-${post.id}`}>
                        <a href={post.link.url} target={'_blank'} rel={'noreferrer'}>
                          {extractDomain(
                            post.link.urlBitlink || post.link.initialUrlBitlink ? post.link.expandedUrl : post.link.url
                          )}
                        </a>
                      </div>
                    </div>
                  </>
                )}
              </>
            ) : (
              post.link && (
                <PostCardLinkPreview
                  size={PostCardLinkPreviewSizeEnum.large}
                  linkUrl={post.link.url}
                  thumbnailUrl={post.link.openGraph?.image || undefined}
                  title={post.link.openGraph?.title || undefined}
                />
              )
            )}

            <div className={'margin-bottom-small-8'}>
              <PostCardStatusTable
                post={post}
                orientation={orientation}
                context={context}
                showMinimalIcons={showMinimalStatusIcons}
              />
            </div>
          </div>
        </div>
        <div className={classNames(classes.DotMenu, classes.DotMenuPortrait)}>
          <ButtonMenu
            isSquareButton={true}
            buttonType={'transparent'}
            listItems={menuListItems}
            side={'right'}
            icon={SquareButtonIconsEnum.dotsShifted}
          />
        </div>
        {showPostStatistics && (
          <PostCardStatisticsTable
            orientation={orientation}
            post={post}
            openDetailsModal={openDetailsModal}
            context={context}
            showBarGraph={showBarGraph}
          />
        )}
        {post.status !== PostStatus.DRAFT &&
          post.status !== PostStatus.PUBLISHED &&
          post.status !== PostStatus.TRASH && (
            <PostCardError
              post={post}
              orientation={orientation}
              isPostStatusUnqueued={isPostStatusUnqueued}
              reschedulePostHandler={reschedulePostHandler}
            />
          )}
      </CategoryBorderCard>
      {isStacked && (
        <div>
          <div className={stackedCardsClasses}></div>
          <div className={stackedCardsClasses}></div>
        </div>
      )}
    </>
  );
};
